import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { 
  AlertCircle, 
  Package, 
  Minus,
  Plus,
  CheckCircle,
  Loader2,
  Search,
  Camera,
  XCircle,
  FileText,
  Trash2,
  RefreshCw,
  X,
  Flashlight,
  ZoomIn,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  User,
  History,
  BarChart3,
  Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Alert,
  AlertDescription,
} from '../components/ui/alert';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

// ==================== Fullscreen Barcode Scanner ====================
const FullscreenScanner = ({ onScan, onClose }) => {
  const html5Ref = useRef(null);
  const [torchOn, setTorchOn] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [error, setError] = useState(null);
  const containerId = 'breakage-barcode-scanner';

  useEffect(() => {
    let mounted = true;

    const startScanner = async () => {
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              video: { facingMode: 'environment' }
            });
            stream.getTracks().forEach(t => t.stop());
          } catch (err) {
            console.warn('Camera permission check failed:', err);
          }
        }

        const scanner = new Html5Qrcode(containerId, {
          verbose: false,
          experimentalFeatures: { useBarCodeDetectorIfSupported: true }
        });

        html5Ref.current = scanner;
        
        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 15,
            qrbox: { width: 280, height: 160 },
            aspectRatio: 1.777,
            disableFlip: false,
          },
          (decodedText) => {
            if (mounted) {
              onScan(decodedText);
            }
          },
          () => {}
        );
      } catch (err) {
        console.error('Scanner start error:', err);
        if (mounted) {
          setError('Não foi possível aceder à câmara. Verifique as permissões.');
        }
      }
    };

    startScanner();

    return () => {
      mounted = false;
      if (html5Ref.current) {
        html5Ref.current.stop().catch(() => {});
      }
    };
  }, [onScan]);

  const toggleTorch = async () => {
    if (!html5Ref.current) return;
    try {
      const videoElement = document.querySelector(`#${containerId} video`);
      if (videoElement && videoElement.srcObject) {
        const track = videoElement.srcObject.getVideoTracks()[0];
        const capabilities = track.getCapabilities();
        if (capabilities.torch) {
          await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
          setTorchOn(!torchOn);
        }
      }
    } catch (err) {
      console.warn('Torch toggle failed:', err);
    }
  };

  const handleZoom = async () => {
    if (!html5Ref.current) return;
    try {
      const videoElement = document.querySelector(`#${containerId} video`);
      if (videoElement && videoElement.srcObject) {
        const track = videoElement.srcObject.getVideoTracks()[0];
        const capabilities = track.getCapabilities();
        if (capabilities.zoom) {
          const newZoom = zoom >= (capabilities.zoom.max || 3) ? 1 : zoom + 0.5;
          await track.applyConstraints({ advanced: [{ zoom: newZoom }] });
          setZoom(newZoom);
        }
      }
    } catch (err) {
      console.warn('Zoom failed:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <div className="flex items-center justify-between p-4 bg-black/80 text-white">
        <span className="font-medium">Ler Código de Barras</span>
        <button onClick={onClose} className="p-2">
          <X className="h-6 w-6" />
        </button>
      </div>
      
      <div className="flex-1 relative">
        <div id={containerId} className="w-full h-full" />
        
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 p-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        )}
        
        <div className="absolute bottom-0 left-0 right-0 p-4 flex justify-center gap-4 bg-gradient-to-t from-black/80 to-transparent">
          <button
            onClick={toggleTorch}
            className={`p-4 rounded-full ${torchOn ? 'bg-yellow-500' : 'bg-white/20'}`}
          >
            <Flashlight className="h-6 w-6 text-white" />
          </button>
          <button
            onClick={handleZoom}
            className="p-4 rounded-full bg-white/20"
          >
            <ZoomIn className="h-6 w-6 text-white" />
            <span className="sr-only">{zoom}x</span>
          </button>
        </div>
      </div>
      
      <div className="p-4 bg-black/80 text-center text-white text-sm">
        Aponte para o código de barras do produto
      </div>
    </div>
  );
};

// ==================== Cart Line Item ====================
const CartLineItem = ({ item, onQuantityChange, onRemove }) => {
  const hasStockError = item.quantity > item.stock_at_creation;
  const shortage = hasStockError ? item.quantity - item.stock_at_creation : 0;
  
  return (
    <div 
      className={`p-3 rounded-lg ${hasStockError ? 'bg-red-50 border border-red-200' : 'bg-slate-50'}`} 
      data-testid={`cart-item-${item.sku}`}
    >
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{item.sku}</p>
          <p className="text-xs text-slate-500 truncate">{item.product_name}</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onQuantityChange(item.sku, item.quantity - 1)}
            disabled={item.quantity <= 1}
            data-testid={`qty-minus-${item.sku}`}
          >
            <Minus className="h-4 w-4" />
          </Button>
          
          <span className={`w-8 text-center font-medium ${hasStockError ? 'text-red-600' : ''}`} data-testid={`qty-value-${item.sku}`}>
            {item.quantity}
          </span>
          
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onQuantityChange(item.sku, item.quantity + 1)}
            data-testid={`qty-plus-${item.sku}`}
          >
            <Plus className="h-4 w-4" />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={() => onRemove(item.sku)}
            data-testid={`remove-${item.sku}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Stock info row */}
      <div className={`mt-2 text-xs flex items-center justify-between ${hasStockError ? 'text-red-600' : 'text-slate-500'}`}>
        <span>Stock disponível: {item.stock_at_creation}</span>
        {hasStockError && (
          <span className="font-medium flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Faltam {shortage}
          </span>
        )}
      </div>
      
      {/* Explicit stock error message */}
      {hasStockError && (
        <div className="mt-2 p-2 bg-red-100 rounded text-xs text-red-700 flex items-start gap-2">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Stock insuficiente neste armazém</p>
            <p className="mt-0.5">Necessário: {item.quantity} · Disponível: {item.stock_at_creation} · Faltam: {shortage}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== Recent Occurrence Card ====================
const OccurrenceCard = ({ occurrence, onClick }) => {
  const isConsumption = occurrence.movement_type === 'consumption';
  const isPosted = occurrence.status === 'posted' || occurrence.status === 'sage_posted';
  const isUnconfirmed = occurrence.status === 'unconfirmed';
  const isFailed = occurrence.status === 'failed';
  const isPending = occurrence.status === 'pending';
  const isCancelled = occurrence.status === 'cancelled';
  const isProcessing = occurrence.status === 'processing';
  
  const getStatusConfig = () => {
    if (isPosted) return { 
      badge: <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Integrado SAGE</Badge>,
      icon: <CheckCircle className="h-4 w-4 text-emerald-600" />,
      borderClass: 'border-emerald-200 hover:border-emerald-300'
    };
    if (isUnconfirmed) return { 
      badge: <Badge className="bg-amber-100 text-amber-700 border-amber-200">Por Confirmar</Badge>,
      icon: <AlertTriangle className="h-4 w-4 text-amber-500" />,
      borderClass: 'border-amber-200 hover:border-amber-300'
    };
    if (isFailed) return { 
      badge: <Badge className="bg-red-100 text-red-700 border-red-200">Falhou</Badge>,
      icon: <XCircle className="h-4 w-4 text-red-500" />,
      borderClass: 'border-red-200 hover:border-red-300'
    };
    if (isCancelled) return { 
      badge: <Badge className="bg-slate-100 text-slate-500 border-slate-200">Cancelado</Badge>,
      icon: null,
      borderClass: 'border-slate-200 opacity-60'
    };
    if (isProcessing) return { 
      badge: <Badge className="bg-blue-100 text-blue-700 border-blue-200">A processar</Badge>,
      icon: <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />,
      borderClass: 'border-blue-200'
    };
    return { 
      badge: <Badge className="bg-slate-100 text-slate-700 border-slate-200">Pendente</Badge>,
      icon: <Clock className="h-4 w-4 text-slate-400" />,
      borderClass: 'border-slate-200 hover:border-slate-300'
    };
  };
  
  const statusConfig = getStatusConfig();
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }) + 
           ' · ' + d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
  };
  
  return (
    <div 
      className={`p-3 sm:p-4 border rounded-lg bg-white cursor-pointer transition-all ${statusConfig.borderClass}`}
      onClick={() => onClick(occurrence)}
      data-testid={`occurrence-${occurrence.id}`}
    >
      {/* Header: Type + Status */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={isConsumption ? 'border-blue-300 text-blue-700' : 'border-orange-300 text-orange-700'}>
            {isConsumption ? 'Consumo' : 'Quebra'}
          </Badge>
          {statusConfig.badge}
        </div>
        {statusConfig.icon}
      </div>
      
      {/* Date */}
      <p className="text-xs text-slate-500 mb-2">{formatDate(occurrence.created_at)}</p>
      
      {/* Info */}
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-sm">
          <User className="h-3 w-3 text-slate-400 flex-shrink-0" />
          <span className="font-medium truncate">{occurrence.responsible_name || occurrence.user_name || 'N/A'}</span>
        </div>
        <p className="text-sm text-slate-600 truncate">{occurrence.reason_label || occurrence.reason}</p>
        <p className="text-xs text-slate-500">
          {occurrence.total_lines || 1} artigo{(occurrence.total_lines || 1) > 1 ? 's' : ''} · {occurrence.total_units || occurrence.quantity || 0} un
        </p>
      </div>
      
      {/* SAGE Reference or Error Hint */}
      {isPosted && occurrence.sage_doc_reference && (
        <p className="text-xs text-emerald-600 font-mono mt-2 truncate">
          📄 {occurrence.sage_doc_reference}
        </p>
      )}
      {(isFailed || isUnconfirmed) && (
        <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
          Ver detalhes →
        </p>
      )}
    </div>
  );
};

// ==================== Occurrence Detail Modal ====================
const OccurrenceDetailModal = ({ occurrence, onClose, onRefresh, user }) => {
  const [loading, setLoading] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const [showTechnical, setShowTechnical] = useState(false);
  const [conditionStatus, setConditionStatus] = useState(null);
  const [showRetryConfirm, setShowRetryConfirm] = useState(false);
  
  if (!occurrence) return null;
  
  const isConsumption = occurrence.movement_type === 'consumption';
  const isPosted = occurrence.status === 'posted' || occurrence.status === 'sage_posted';
  const isUnconfirmed = occurrence.status === 'unconfirmed';
  const isFailed = occurrence.status === 'failed';
  const isPending = occurrence.status === 'pending';
  const isCancelled = occurrence.status === 'cancelled';
  
  const isAdmin = user?.role === 'admin';
  const lines = occurrence.lines || [{ sku: occurrence.sku, product_name: occurrence.product_name, quantity: occurrence.quantity }];
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  
  // Error categories matching backend
  const ErrorCategory = {
    INSUFFICIENT_STOCK: 'INSUFFICIENT_STOCK',
    PRODUCT_NOT_FOUND: 'PRODUCT_NOT_FOUND',
    SAGE_CONFIGURATION: 'SAGE_CONFIGURATION',
    COMMUNICATION_ERROR: 'COMMUNICATION_ERROR',
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    UNKNOWN: 'UNKNOWN',
    UNCONFIRMED: 'UNCONFIRMED',
  };
  
  // Classify error for user-friendly display
  const getErrorInfo = () => {
    const errMsg = occurrence.error_message || '';
    const errLower = errMsg.toLowerCase();
    
    if (errLower.includes('quantidade suficiente') || errLower.includes('stock insuficiente') || errLower.includes('insufficient') || errLower.includes('não existe quantidade')) {
      return {
        category: ErrorCategory.INSUFFICIENT_STOCK,
        userMessage: 'Stock disponível insuficiente para concluir a integração.',
        hint: 'Regularize a diferença de stock ou contacte o responsável de stocks. Depois poderá verificar novamente a situação.',
        retryableNow: false,
        canCheckCondition: true,
        checkButtonLabel: 'Verificar stock no SAGE',
      };
    }
    if (errLower.includes('artigo não encontrado') || errLower.includes('produto não encontrado') || errLower.includes('not found')) {
      return {
        category: ErrorCategory.PRODUCT_NOT_FOUND,
        userMessage: 'O artigo não foi encontrado no SAGE.',
        hint: 'Verifique se o SKU existe e está ativo no SAGE.',
        retryableNow: false,
        canCheckCondition: false,
      };
    }
    if (errLower.includes('timeout') || errLower.includes('timed out') || errLower.includes('connection') || errLower.includes('network') || errLower.includes('comunicação')) {
      return {
        category: ErrorCategory.COMMUNICATION_ERROR,
        userMessage: 'Não foi possível comunicar com o SAGE.',
        hint: 'Use "Confirmar no SAGE" antes de tentar novamente para evitar duplicados.',
        retryableNow: false,
        canCheckCondition: false,
        needsReconciliation: true,
      };
    }
    if (errLower.includes('terceiro') || errLower.includes('tipo de preço') || errLower.includes('pcm') || errLower.includes('tipopreco') || errLower.includes('não indicado')) {
      return {
        category: ErrorCategory.SAGE_CONFIGURATION,
        userMessage: 'Erro de configuração na integração SAGE.',
        hint: 'Use "Verificar situação" para confirmar se o problema foi corrigido.',
        retryableNow: false,
        canCheckCondition: true,
        checkButtonLabel: 'Verificar situação',
      };
    }
    // Default - unknown, never retry blindly
    return {
      category: ErrorCategory.UNKNOWN,
      userMessage: 'Ocorreu um erro na integração com o SAGE.',
      hint: 'Contacte o suporte técnico para análise.',
      retryableNow: false,
      canCheckCondition: false,
    };
  };
  
  const errorInfo = (isFailed || isUnconfirmed) ? getErrorInfo() : null;
  
  // Handle check condition action
  const handleCheckCondition = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    setLoading(true);
    setActionResult(null);
    
    try {
      const res = await axios.post(
        `${API}/api/breakage/${occurrence.id}/check-condition`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add timestamp to the response
      const resultWithTimestamp = {
        ...res.data,
        checkedAt: new Date().toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }),
      };
      setConditionStatus(resultWithTimestamp);
      
      if (res.data.condition_resolved) {
        setActionResult({ 
          type: 'success', 
          message: 'Stock suficiente. A ocorrência já pode ser integrada.'
        });
      } else {
        setActionResult({ 
          type: 'warning', 
          message: 'Stock ainda insuficiente.'
        });
      }
    } catch (err) {
      setActionResult({ 
        type: 'error', 
        message: err.response?.data?.detail || err.message || 'Erro ao verificar condição' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle retry with confirmation
  const handleRetryWithConfirm = () => {
    setShowRetryConfirm(true);
  };
  
  // Handle actual retry action
  const handleRetry = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    setShowRetryConfirm(false);
    setLoading(true);
    setActionResult({ type: 'info', message: 'Integrando no SAGE...' });;
    setActionResult(null);
    
    try {
      const res = await axios.post(
        `${API}/api/breakage/${occurrence.id}/retry`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        setActionResult({ 
          type: 'success', 
          message: res.data.sage_reference 
            ? `Integrado com sucesso: ${res.data.sage_reference}` 
            : 'Integração bem sucedida'
        });
        if (onRefresh) setTimeout(onRefresh, 1500);
      } else {
        setActionResult({ type: 'error', message: res.data.message || 'Erro ao re-tentar' });
      }
    } catch (err) {
      setActionResult({ 
        type: 'error', 
        message: err.response?.data?.detail || err.message || 'Erro ao re-tentar' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle reconcile action (for UNCONFIRMED)
  const handleReconcile = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    setLoading(true);
    setActionResult(null);
    
    try {
      const res = await axios.post(
        `${API}/api/breakage/${occurrence.id}/reconcile`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.found) {
        setActionResult({ 
          type: 'success', 
          message: `Documento encontrado no SAGE: ${res.data.sage_reference}` 
        });
        if (onRefresh) setTimeout(onRefresh, 1500);
      } else if (res.data.can_retry) {
        setActionResult({ 
          type: 'info', 
          message: 'Documento não encontrado no SAGE. Pode tentar novamente em segurança.' 
        });
      } else {
        setActionResult({ 
          type: 'warning', 
          message: res.data.message || 'Não foi possível confirmar o estado no SAGE.' 
        });
      }
    } catch (err) {
      setActionResult({ 
        type: 'error', 
        message: err.response?.data?.detail || err.message || 'Erro na reconciliação' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle cancel action
  const handleCancel = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    if (!window.confirm('Tem a certeza que pretende cancelar esta ocorrência?')) return;
    
    setLoading(true);
    setActionResult(null);
    
    try {
      const res = await axios.delete(
        `${API}/api/breakage/${occurrence.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setActionResult({ type: 'success', message: 'Ocorrência cancelada' });
      if (onRefresh) setTimeout(onRefresh, 1500);
    } catch (err) {
      setActionResult({ 
        type: 'error', 
        message: err.response?.data?.detail || err.message || 'Erro ao cancelar' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Status display config
  const getStatusDisplay = () => {
    if (isPosted) return { label: 'Integrado SAGE', color: 'text-emerald-700 bg-emerald-50 border-emerald-200', icon: <CheckCircle className="h-4 w-4" /> };
    if (isUnconfirmed) return { label: 'Por Confirmar', color: 'text-amber-700 bg-amber-50 border-amber-200', icon: <AlertTriangle className="h-4 w-4" /> };
    if (isFailed) return { label: 'Falhou', color: 'text-red-700 bg-red-50 border-red-200', icon: <XCircle className="h-4 w-4" /> };
    if (isCancelled) return { label: 'Cancelado', color: 'text-slate-500 bg-slate-50 border-slate-200', icon: null };
    if (occurrence.status === 'processing') return { label: 'A processar', color: 'text-blue-700 bg-blue-50 border-blue-200', icon: <Loader2 className="h-4 w-4 animate-spin" /> };
    return { label: 'Pendente', color: 'text-slate-700 bg-slate-50 border-slate-200', icon: <Clock className="h-4 w-4" /> };
  };
  
  const statusDisplay = getStatusDisplay();
  
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] sm:max-h-[85vh] overflow-hidden flex flex-col" data-testid="occurrence-detail-modal">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold">Detalhe da {isConsumption ? 'Consumo' : 'Quebra'}</span>
            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${statusDisplay.color}`}>
              {statusDisplay.icon}
              {statusDisplay.label}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Basic Info Grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide">Data</p>
              <p className="font-medium">{formatDate(occurrence.created_at)}</p>
            </div>
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide">Armazém</p>
              <p className="font-medium">{occurrence.warehouse_code}{occurrence.store_name ? ` — ${occurrence.store_name}` : ''}</p>
            </div>
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide">Responsável</p>
              <p className="font-medium">{occurrence.responsible_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide">Registado por</p>
              <p className="font-medium truncate">{occurrence.created_by_name || occurrence.user_name}</p>
            </div>
            <div className="col-span-2">
              <p className="text-slate-500 text-xs uppercase tracking-wide">Motivo</p>
              <p className="font-medium">{occurrence.reason_label || occurrence.reason}</p>
            </div>
            {occurrence.observation && (
              <div className="col-span-2">
                <p className="text-slate-500 text-xs uppercase tracking-wide">Observação</p>
                <p className="font-medium">{occurrence.observation}</p>
              </div>
            )}
          </div>
          
          {/* Artigos Section */}
          <div>
            <p className="text-slate-500 text-xs uppercase tracking-wide mb-2">Artigos ({lines.length})</p>
            <div className="space-y-2">
              {lines.map((ln, idx) => (
                <div key={idx} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-sm font-medium truncate">{ln.sku}</p>
                    <p className="text-xs text-slate-500 truncate">{ln.product_name}</p>
                  </div>
                  <Badge variant="secondary" className="ml-2 flex-shrink-0">{ln.quantity} un</Badge>
                </div>
              ))}
            </div>
          </div>
          
          {/* SAGE Integration Section */}
          <div className="border-t pt-4">
            <p className="text-slate-500 text-xs uppercase tracking-wide mb-2">Integração SAGE</p>
            
            {isPosted && occurrence.sage_doc_reference && (
              <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-emerald-800">Documento criado</p>
                  <p className="font-mono text-emerald-700">{occurrence.sage_doc_reference}</p>
                </div>
              </div>
            )}
            
            {isFailed && errorInfo && !conditionStatus?.can_retry && (
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-start gap-2">
                  <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800">Falhou a criar documento</p>
                    <p className="text-sm text-red-700 mt-1">{errorInfo.userMessage}</p>
                    <p className="text-xs text-red-600 mt-1">{errorInfo.hint}</p>
                    
                    {/* Show stock details if available from check-condition */}
                    {conditionStatus?.stock_details && (
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-slate-700">Detalhes de Stock:</p>
                          {conditionStatus.checkedAt && (
                            <p className="text-[10px] text-slate-400">Verificado às {conditionStatus.checkedAt}</p>
                          )}
                        </div>
                        {conditionStatus.stock_details.map((item, idx) => {
                          const missing = item.quantity_required - (item.stock_available || 0);
                          return (
                            <div key={idx} className="p-2 bg-white rounded border border-slate-200">
                              <p className="font-mono text-xs font-medium text-slate-800 mb-1">{item.sku}</p>
                              <div className="grid grid-cols-3 gap-2 text-xs">
                                <div>
                                  <p className="text-slate-400">Necessário</p>
                                  <p className="font-medium text-slate-700">{item.quantity_required}</p>
                                </div>
                                <div>
                                  <p className="text-slate-400">Disponível</p>
                                  <p className={`font-medium ${item.sufficient ? 'text-emerald-600' : 'text-slate-700'}`}>
                                    {item.stock_available ?? '?'}
                                  </p>
                                </div>
                                {item.sufficient ? (
                                  <div className="flex items-center justify-end">
                                    <span className="text-emerald-600 font-medium flex items-center gap-1">
                                      <CheckCircle className="h-4 w-4" /> OK
                                    </span>
                                  </div>
                                ) : (
                                  <div>
                                    <p className="text-slate-400">Faltam</p>
                                    <p className="font-bold text-red-600">{missing > 0 ? missing : '?'}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Show config check result */}
                    {conditionStatus?.successful_integrations_after != null && (
                      <div className="mt-3 p-2 bg-white rounded border border-slate-200">
                        <p className="text-xs text-slate-600">
                          {conditionStatus.successful_integrations_after > 0 
                            ? `✓ ${conditionStatus.successful_integrations_after} integrações bem-sucedidas posteriores. Configuração corrigida.`
                            : '✗ Nenhuma integração bem-sucedida posterior. Problema pode persistir.'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Show success state when condition is resolved */}
            {isFailed && conditionStatus?.can_retry && (
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-emerald-800">Stock suficiente</p>
                    <p className="text-sm text-emerald-700 mt-1">A ocorrência já pode ser integrada no SAGE.</p>
                    {conditionStatus.checkedAt && (
                      <p className="text-xs text-emerald-600 mt-1">Verificado às {conditionStatus.checkedAt}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {isUnconfirmed && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800">Por confirmar</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Não foi possível confirmar se o documento foi criado no SAGE.
                    </p>
                    <p className="text-xs text-amber-600 mt-1">
                      Use "Confirmar no SAGE" para verificar o estado.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {isPending && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-600">Aguardando processamento.</p>
              </div>
            )}
            
            {isCancelled && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-500">Esta ocorrência foi cancelada.</p>
              </div>
            )}
          </div>
          
          {/* Technical Details (Admin only) */}
          {isAdmin && (isFailed || isUnconfirmed) && occurrence.error_message && (
            <div className="border-t pt-4">
              <button
                onClick={() => setShowTechnical(!showTechnical)}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
              >
                {showTechnical ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                Detalhes técnicos
              </button>
              {showTechnical && (
                <div className="mt-2 p-2 bg-slate-800 rounded text-xs font-mono text-slate-300 overflow-x-auto max-h-32 overflow-y-auto">
                  {occurrence.error_message}
                </div>
              )}
            </div>
          )}
          
          {/* Action Result */}
          {actionResult && (
            <Alert className={
              actionResult.type === 'success' ? 'bg-emerald-50 border-emerald-200' :
              actionResult.type === 'info' ? 'bg-blue-50 border-blue-200' :
              actionResult.type === 'warning' ? 'bg-amber-50 border-amber-200' :
              'bg-red-50 border-red-200'
            }>
              <AlertDescription className={
                actionResult.type === 'success' ? 'text-emerald-800' :
                actionResult.type === 'info' ? 'text-blue-800' :
                actionResult.type === 'warning' ? 'text-amber-800' :
                'text-red-800'
              }>
                {actionResult.message}
              </AlertDescription>
            </Alert>
          )}
        </div>
        
        {/* Footer with Actions */}
        <div className="p-4 border-t bg-slate-50 flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-2">
            {/* FAILED: First action is "Check Condition" for stock/config errors */}
            {isFailed && errorInfo?.canCheckCondition && !conditionStatus?.can_retry && (
              <Button
                onClick={handleCheckCondition}
                disabled={loading}
                variant="outline"
                className="flex-1 h-11"
                data-testid="check-condition-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                {errorInfo?.checkButtonLabel || 'Verificar situação'}
              </Button>
            )}
            
            {/* FAILED + condition resolved: Show Retry with confirmation */}
            {isFailed && conditionStatus?.can_retry && (
              <Button
                onClick={handleRetryWithConfirm}
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700 flex-1 h-11"
                data-testid="retry-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Tentar novamente
              </Button>
            )}
            
            {/* FAILED + communication error: Reconcile first */}
            {isFailed && errorInfo?.needsReconciliation && (
              <Button
                onClick={handleReconcile}
                disabled={loading}
                variant="outline"
                className="flex-1 h-11"
                data-testid="reconcile-failed-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                Confirmar no SAGE
              </Button>
            )}
            
            {/* UNCONFIRMED: Reconcile only */}
            {isUnconfirmed && (
              <>
                <Button
                  onClick={handleReconcile}
                  disabled={loading}
                  className="bg-amber-600 hover:bg-amber-700 flex-1 h-11"
                  data-testid="reconcile-button"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                  Confirmar no SAGE
                </Button>
                {actionResult?.type === 'info' && actionResult?.message?.includes('pode tentar') && (
                  <Button
                    onClick={handleRetry}
                    disabled={loading}
                    variant="outline"
                    className="flex-1 h-11 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="retry-after-reconcile-button"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Tentar novamente
                  </Button>
                )}
              </>
            )}
            
            {/* PENDING or non-checkable FAILED: Cancel option */}
            {(isPending || (isFailed && !errorInfo?.canCheckCondition && !errorInfo?.needsReconciliation)) && (
              <Button
                onClick={handleCancel}
                disabled={loading}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50 flex-1 h-11"
                data-testid="cancel-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Cancelar
              </Button>
            )}
            
            <Button
              onClick={onClose}
              variant="outline"
              className="h-11"
              data-testid="close-detail-button"
            >
              Fechar
            </Button>
          </div>
        </div>
        
        {/* Retry Confirmation Dialog */}
        {showRetryConfirm && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 rounded-2xl">
            <div className="bg-white rounded-xl p-4 max-w-sm w-full shadow-lg">
              <h3 className="text-base font-semibold text-slate-900 mb-2">
                Tentar novamente a integração desta {isConsumption ? 'consumo' : 'quebra'} no SAGE?
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Será utilizada a ocorrência original, sem alterar artigos ou quantidades.
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowRetryConfirm(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleRetry}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  Integrar novamente
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== Main Component ====================
export default function Breakage() {
  const navigate = useNavigate();
  
  // Auth
  const [user, setUser] = useState(null);
  const getToken = useCallback(() => localStorage.getItem('token'), []);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Form state - HEADER
  const [movementType, setMovementType] = useState('breakage'); // breakage or consumption
  const [reason, setReason] = useState('');
  const [responsibleName, setResponsibleName] = useState('');
  const [observation, setObservation] = useState('');
  const [reasons, setReasons] = useState([]);
  
  // Form state - CART (lines)
  const [cartItems, setCartItems] = useState([]);
  
  // Product search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  
  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Recent occurrences
  const [recentOccurrences, setRecentOccurrences] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [selectedOccurrence, setSelectedOccurrence] = useState(null);
  const [attentionCount, setAttentionCount] = useState({ total: 0, failed: 0, unconfirmed: 0 });
  
  // Load user
  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      try {
        setUser(JSON.parse(stored));
      } catch (e) {}
    }
  }, []);
  
  // Get user's reference warehouse
  const userReferenceWarehouse = user?.reference_warehouse || user?.default_warehouse;
  
  // Load recent occurrences
  const loadRecentOccurrences = useCallback(async () => {
    const token = getToken();
    if (!token || !user) return;
    
    setLoadingRecent(true);
    try {
      const perms = user?.permissions || [];
      const role = user?.role;
      const canSeeAll = role === 'admin' || perms.includes('breakage.create_any') || perms.includes('warehouses.view_all');
      const queryParams = canSeeAll ? 'limit=10' : 'limit=10&mine_only=true';
      
      const [occResponse, attentionResponse] = await Promise.all([
        axios.get(`${API}/api/breakage?${queryParams}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/api/breakage/attention/count`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { total: 0, failed: 0, unconfirmed: 0 } })),
      ]);
      
      setRecentOccurrences(occResponse.data.items || []);
      setAttentionCount(attentionResponse.data || { total: 0, failed: 0, unconfirmed: 0 });
    } catch (err) {
      console.error('Failed to load recent occurrences:', err);
    } finally {
      setLoadingRecent(false);
    }
  }, [getToken, user]);
  
  useEffect(() => {
    if (user) {
      loadRecentOccurrences();
    }
  }, [user, loadRecentOccurrences]);
  
  // Load occurrence detail from query param (when coming from history page)
  useEffect(() => {
    const detailId = searchParams.get('detail');
    if (detailId && user) {
      const loadOccurrenceDetail = async () => {
        const token = getToken();
        if (!token) return;
        
        try {
          const response = await axios.get(`${API}/api/breakage/${detailId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.data) {
            setSelectedOccurrence(response.data);
            // Clear the query param
            setSearchParams({});
          }
        } catch (err) {
          console.error('Failed to load occurrence detail:', err);
        }
      };
      
      loadOccurrenceDetail();
    }
  }, [searchParams, user, getToken, setSearchParams]);
  
  // Load reasons
  useEffect(() => {
    const loadReasons = async () => {
      const token = getToken();
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/api/breakage/reasons`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setReasons(response.data.reasons || []);
      } catch (err) {
        console.error('Failed to load reasons:', err);
      }
    };
    
    if (user) {
      loadReasons();
    }
  }, [getToken, user]);
  
  // Filter reasons by movement type
  const breakageReasons = reasons.filter(r => r.movement_type !== 'consumption');
  const consumptionReasons = reasons.filter(r => r.movement_type === 'consumption');
  const currentReasons = movementType === 'consumption' ? consumptionReasons : breakageReasons;
  
  // Selected reason details
  const selectedReason = reasons.find(r => r.code === reason);
  
  // Product search with debounce
  // Filter by user's warehouse and only show products with stock > 0
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q || q.length < 2 || !userReferenceWarehouse) {
      setSearchResults([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const token = getToken();
        const response = await axios.get(
          `${API}/api/products?search=${encodeURIComponent(q)}&warehouse=${userReferenceWarehouse}&hide_zero_stock=true&page_size=100`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSearchResults(response.data.items || response.data.products || []);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery, userReferenceWarehouse, getToken]);
  
  // Handle barcode scan
  const handleScan = async (code) => {
    setShowScanner(false);
    setSearchQuery('');
    setSearchResults([]);
    
    const token = getToken();
    if (!token) return;
    
    try {
      // Try to find product by scanned code
      const response = await axios.get(
        `${API}/api/breakage/product/${encodeURIComponent(code)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data) {
        addToCart(response.data);
      }
    } catch (err) {
      // Try inventory search as fallback
      try {
        const invResponse = await axios.get(
          `${API}/api/products?search=${encodeURIComponent(code)}&warehouse=${userReferenceWarehouse}&hide_zero_stock=true&page_size=1`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if ((invResponse.data.items?.length || invResponse.data.products?.length) > 0) {
          const product = invResponse.data.items?.[0] || invResponse.data.products?.[0];
          addToCart({
            sku: product.sku,
            name: product.name,
            stock_in_warehouse: (product.stock_by_warehouse || {})[userReferenceWarehouse] || 0,
          });
        } else {
          setError(`Produto não encontrado: ${code}`);
        }
      } catch (e) {
        setError(`Produto não encontrado: ${code}`);
      }
    }
  };
  
  // Add product to cart
  const addToCart = (product) => {
    const sku = product.sku;
    const existingIndex = cartItems.findIndex(item => item.sku === sku);
    
    if (existingIndex >= 0) {
      // Increment quantity if already in cart
      const updated = [...cartItems];
      updated[existingIndex] = {
        ...updated[existingIndex],
        quantity: updated[existingIndex].quantity + 1,
      };
      setCartItems(updated);
    } else {
      // Add new item
      setCartItems([...cartItems, {
        sku: sku,
        product_name: product.name || product.product_name,
        quantity: 1,
        stock_at_creation: product.stock_in_warehouse || 0,
      }]);
    }
    
    setSearchQuery('');
    setSearchResults([]);
    setError(null);
  };
  
  // Update cart item quantity
  const updateQuantity = (sku, newQuantity) => {
    if (newQuantity < 1) return;
    
    setCartItems(cartItems.map(item => 
      item.sku === sku ? { ...item, quantity: newQuantity } : item
    ));
  };
  
  // Remove from cart
  const removeFromCart = (sku) => {
    setCartItems(cartItems.filter(item => item.sku !== sku));
  };
  
  // Calculate totals
  const totalItems = cartItems.length;
  const totalUnits = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  
  // Check for stock errors (Parte A - Validação Preventiva)
  const cartStockErrors = cartItems.filter(item => item.quantity > item.stock_at_creation);
  const hasStockErrors = cartStockErrors.length > 0;
  
  // Validate form
  const isFormValid = () => {
    return (
      reason &&
      responsibleName.trim() &&
      cartItems.length > 0 &&
      !hasStockErrors &&  // Block if any item exceeds available stock
      (!selectedReason?.requires_observation || observation.trim())
    );
  };
  
  // Get form validation message
  const getValidationMessage = () => {
    if (!reason) return 'Selecione um motivo';
    if (!responsibleName.trim()) return 'Indique o responsável';
    if (cartItems.length === 0) return 'Adicione pelo menos um artigo';
    if (hasStockErrors) {
      const errorCount = cartStockErrors.length;
      return `Stock disponível insuficiente em ${errorCount} artigo${errorCount > 1 ? 's' : ''}`;
    }
    if (selectedReason?.requires_observation && !observation.trim()) return 'Observação obrigatória para este motivo';
    return null;
  };
  
  // Submit occurrence
  const handleSubmit = async () => {
    // Check for stock errors first
    if (hasStockErrors) {
      setError('Stock disponível insuficiente neste armazém. Corrija as quantidades antes de registar.');
      return;
    }
    
    if (!isFormValid()) {
      setError(getValidationMessage() || 'Por favor preencha todos os campos obrigatórios');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);
    
    const token = getToken();
    if (!token) {
      setError('Sessão expirada. Faça login novamente.');
      setSubmitting(false);
      return;
    }
    
    try {
      const payload = {
        movement_type: movementType,
        reason: reason,
        responsible_name: responsibleName.trim(),
        lines: cartItems.map(item => ({
          sku: item.sku,
          quantity: item.quantity,
        })),
        warehouse_code: userReferenceWarehouse,
        observation: observation.trim() || null,
        integrate_sage: true,
      };
      
      const response = await axios.post(
        `${API}/api/breakage/occurrence`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Success
      const result = response.data;
      const sageRef = result.occurrence?.sage_doc_reference;
      
      if (sageRef) {
        setSuccessMessage(`${movementType === 'consumption' ? 'Consumo' : 'Quebra'} registada e integrada: ${sageRef}`);
      } else if (result.occurrence?.status === 'unconfirmed') {
        setSuccessMessage('Registada. Integração SAGE por confirmar.');
      } else {
        setSuccessMessage(result.message || 'Registada com sucesso');
      }
      
      // Reset form
      setCartItems([]);
      setReason('');
      setResponsibleName('');
      setObservation('');
      
      // Reload recent
      loadRecentOccurrences();
      
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.detail || 'Erro ao registar. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Movement label
  const movementLabel = movementType === 'consumption' ? 'Consumo' : 'Quebra';
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Scanner Modal */}
      {showScanner && (
        <FullscreenScanner onScan={handleScan} onClose={() => setShowScanner(false)} />
      )}
      
      {/* Occurrence Detail Modal */}
      {selectedOccurrence && (
        <OccurrenceDetailModal 
          occurrence={selectedOccurrence} 
          onClose={() => setSelectedOccurrence(null)}
          onRefresh={loadRecentOccurrences}
          user={user}
        />
      )}
      
      <div className="max-w-7xl mx-auto p-4 lg:p-6">
        {/* Attention Alert */}
        {attentionCount.total > 0 && (
          <div 
            className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between gap-3 cursor-pointer hover:bg-amber-100 transition-colors"
            onClick={() => navigate('/breakage/history?status=failed')}
            data-testid="attention-alert"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <span className="text-sm text-amber-800">
                <strong>{attentionCount.total}</strong> ocorrência{attentionCount.total > 1 ? 's' : ''} necessita{attentionCount.total > 1 ? 'm' : ''} de atenção
                {attentionCount.failed > 0 && <span className="text-amber-600"> ({attentionCount.failed} falhou)</span>}
                {attentionCount.unconfirmed > 0 && <span className="text-amber-600"> ({attentionCount.unconfirmed} por confirmar)</span>}
              </span>
            </div>
            <ChevronRight className="h-4 w-4 text-amber-600 flex-shrink-0" />
          </div>
        )}
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Quebras e Consumos</h1>
            <p className="text-slate-500 text-sm mt-1">
              Registe quebras de stock e consumos internos
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/breakage/history')}
            className="hidden sm:flex items-center gap-2"
          >
            <History className="h-4 w-4" />
            Histórico
          </Button>
        </div>
        
        {/* Messages */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {successMessage && (
          <Alert className="mb-4 border-green-200 bg-green-50 text-green-800">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription>{successMessage}</AlertDescription>
          </Alert>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Form */}
          <div className="lg:col-span-2 space-y-4">
            {/* SECTION A: Occurrence Header */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Dados da Ocorrência</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Type Toggle */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Tipo</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={movementType === 'breakage' ? 'default' : 'outline'}
                      onClick={() => { setMovementType('breakage'); setReason(''); }}
                      className="flex-1"
                      data-testid="type-breakage"
                    >
                      Quebra
                    </Button>
                    <Button
                      type="button"
                      variant={movementType === 'consumption' ? 'default' : 'outline'}
                      onClick={() => { setMovementType('consumption'); setReason(''); }}
                      className="flex-1"
                      data-testid="type-consumption"
                    >
                      Consumo
                    </Button>
                  </div>
                </div>
                
                {/* Warehouse (read-only) */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Armazém</Label>
                  <Input 
                    value={user?.reference_warehouse_name || userReferenceWarehouse || 'Não definido'} 
                    disabled 
                    className="bg-slate-50"
                  />
                </div>
                
                {/* Reason */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Motivo *</Label>
                  <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger data-testid="reason-select">
                      <SelectValue placeholder="Selecione o motivo" />
                    </SelectTrigger>
                    <SelectContent>
                      {currentReasons.map(r => (
                        <SelectItem key={r.code} value={r.code}>
                          {r.label_pt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Responsible Name (free text) */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Responsável *</Label>
                  <Input
                    value={responsibleName}
                    onChange={(e) => setResponsibleName(e.target.value)}
                    placeholder="Nome da pessoa responsável"
                    data-testid="responsible-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Pessoa associada a esta ocorrência
                  </p>
                </div>
                
                {/* Observation */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    Observação {selectedReason?.requires_observation ? '*' : ''}
                  </Label>
                  <Textarea
                    value={observation}
                    onChange={(e) => setObservation(e.target.value)}
                    placeholder="Notas adicionais..."
                    rows={2}
                    data-testid="observation-input"
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* SECTION B: Cart Items */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Artigos</span>
                  {totalItems > 0 && (
                    <Badge variant="secondary">
                      {totalItems} artigo{totalItems > 1 ? 's' : ''} · {totalUnits} unidade{totalUnits > 1 ? 's' : ''}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search / Scanner */}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Pesquisar SKU ou descrição..."
                      className="pl-9"
                      data-testid="product-search"
                    />
                    {searching && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setShowScanner(true)}
                    className="shrink-0"
                    data-testid="scanner-button"
                  >
                    <Camera className="h-5 w-5" />
                  </Button>
                </div>
                
                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="border rounded-lg divide-y max-h-64 sm:max-h-80 overflow-y-auto">
                    {searchResults.map(product => {
                      const stockInWarehouse = (product.stock_by_warehouse || {})[userReferenceWarehouse] || 0;
                      return (
                        <button
                          key={product.sku}
                          onClick={() => addToCart({
                            sku: product.sku,
                            name: product.name,
                            stock_in_warehouse: stockInWarehouse,
                          })}
                          className="w-full p-3 text-left hover:bg-slate-50 flex items-center justify-between gap-2"
                          data-testid={`search-result-${product.sku}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm truncate">{product.sku}</p>
                            <p className="text-xs text-slate-500 truncate">{product.name}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`text-xs font-medium px-2 py-1 rounded ${
                              stockInWarehouse > 0 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {stockInWarehouse} un.
                            </span>
                            <Plus className="h-4 w-4 text-slate-400" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                
                {/* Cart Items */}
                {cartItems.length > 0 ? (
                  <div className="space-y-2" data-testid="cart-items">
                    {cartItems.map(item => (
                      <CartLineItem
                        key={item.sku}
                        item={item}
                        onQuantityChange={updateQuantity}
                        onRemove={removeFromCart}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Package className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm">Pesquise ou escaneie artigos para adicionar</p>
                  </div>
                )}
                
                {/* Stock Error Summary */}
                {hasStockErrors && cartItems.length > 0 && (
                  <Alert variant="destructive" className="mt-2" data-testid="stock-error-alert">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <p className="font-medium">Stock disponível insuficiente neste armazém</p>
                      <p className="text-sm mt-1">
                        Corrija as quantidades dos artigos assinalados a vermelho antes de registar a ocorrência.
                      </p>
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Submit Button */}
                <div className="pt-2 border-t">
                  <Button
                    onClick={handleSubmit}
                    disabled={!isFormValid() || submitting}
                    className={`w-full h-12 text-base ${hasStockErrors ? 'bg-slate-400 cursor-not-allowed' : ''}`}
                    data-testid="submit-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        A registar...
                      </>
                    ) : hasStockErrors ? (
                      <>
                        <AlertCircle className="h-5 w-5 mr-2" />
                        Stock insuficiente — Corrija as quantidades
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Registar {movementLabel} — {totalItems} artigo{totalItems !== 1 ? 's' : ''} / {totalUnits} unidade{totalUnits !== 1 ? 's' : ''}
                      </>
                    )}
                  </Button>
                  
                  {/* Validation hint */}
                  {!isFormValid() && !hasStockErrors && cartItems.length > 0 && getValidationMessage() && (
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      {getValidationMessage()}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Right Panel - Recent Occurrences */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Registos Recentes
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={loadRecentOccurrences}
                    disabled={loadingRecent}
                  >
                    <RefreshCw className={`h-4 w-4 ${loadingRecent ? 'animate-spin' : ''}`} />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingRecent ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                  </div>
                ) : recentOccurrences.length > 0 ? (
                  <div className="space-y-3">
                    {recentOccurrences.map(occ => (
                      <OccurrenceCard
                        key={occ.id}
                        occurrence={occ}
                        onClick={setSelectedOccurrence}
                      />
                    ))}
                    
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => navigate('/breakage/history')}
                    >
                      Ver histórico completo
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm">Nenhum registo recente</p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Mobile History Button */}
            <Button
              variant="outline"
              onClick={() => navigate('/breakage/history')}
              className="w-full sm:hidden flex items-center justify-center gap-2"
            >
              <History className="h-4 w-4" />
              Ver Histórico
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
